import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_URL = `${BACKEND_URL}/api`;

/**
 * Admin auth uses an HttpOnly session cookie set by the backend on login.
 * Nothing secret is kept in localStorage/sessionStorage, so page scripts (and XSS) can't read it.
 * `withCredentials` makes the browser attach the cookie to every /api call.
 */
const api = axios.create({ baseURL: API_URL, timeout: 20000, withCredentials: true });

api.interceptors.response.use(
  (res) => res,
  (err) => {
    // Let the auth context know the session is gone (expired / logged out elsewhere)
    if (err?.response?.status === 401 && !err.config?.url?.startsWith("/auth/")) {
      window.dispatchEvent(new Event("avalon:unauthorized"));
    }
    return Promise.reject(err);
  }
);

export const errorMessage = (err, fallback = "Something went wrong") => {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d.length) return d.map((x) => x.msg || JSON.stringify(x)).join(", ");
  return err?.message || fallback;
};

// ---- Auth
export const authApi = {
  login: (username, password) => api.post("/auth/login", { username, password }).then((r) => r.data),
  googleSession: (session_id) => api.post("/auth/google/session", { session_id }).then((r) => r.data),
  logout: () => api.post("/auth/logout").then((r) => r.data),
  me: () => api.get("/auth/me").then((r) => r.data),
  changePassword: (current_password, new_password) =>
    api.post("/auth/change-password", { current_password, new_password }).then((r) => r.data),
};

// ---- Team (Google sign-in allow-list, master admin only for changes)
export const teamApi = {
  list: () => api.get("/admin/team").then((r) => r.data),
  add: (email, name) => api.post("/admin/team", { email, name }).then((r) => r.data),
  remove: (id) => api.delete(`/admin/team/${id}`).then((r) => r.data),
};

// ---- Form submissions inbox
export const submissionsApi = {
  list: (params) => api.get("/admin/submissions", { params }).then((r) => r.data),
  get: (id) => api.get(`/admin/submissions/${id}`).then((r) => r.data),
  update: (id, body) => api.patch(`/admin/submissions/${id}`, body).then((r) => r.data),
  remove: (id) => api.delete(`/admin/submissions/${id}`).then((r) => r.data),
  forward: (id, to, note) => api.post(`/admin/submissions/${id}/forward`, { to, note }).then((r) => r.data),
  exportUrl: (formType) => `${API_URL}/admin/submissions/export.csv${formType ? `?form_type=${formType}` : ""}`,
};

// ---- Form routing + email templates
export const formsApi = {
  settings: () => api.get("/admin/forms/settings").then((r) => r.data),
  updateSettings: (formType, body) => api.put(`/admin/forms/settings/${formType}`, body).then((r) => r.data),
  emailStatus: () => api.get("/admin/email/status").then((r) => r.data),
  testEmail: (to) => api.post("/admin/forms/test-email", { to }).then((r) => r.data),
  addDomain: (name) => api.post("/admin/email/domain", { name }).then((r) => r.data),
  verifyDomain: () => api.post("/admin/email/domain/verify", null, { timeout: 60000 }).then((r) => r.data),
  saveSender: (sender_name, sender_email) => api.put("/admin/email/sender", { sender_name, sender_email }).then((r) => r.data),
};

// ---- Admin pages
export const pagesApi = {
  overview: () => api.get("/admin/overview").then((r) => r.data),
  list: () => api.get("/admin/pages").then((r) => r.data),
  get: (id) => api.get(`/admin/pages/${id}`).then((r) => r.data),
  create: (body) => api.post("/admin/pages", body).then((r) => r.data),
  update: (id, body) => api.put(`/admin/pages/${id}`, body).then((r) => r.data),
  remove: (id) => api.delete(`/admin/pages/${id}`).then((r) => r.data),
  duplicate: (id) => api.post(`/admin/pages/${id}/duplicate`).then((r) => r.data),
  stats: (id, days = 30) => api.get(`/admin/pages/${id}/stats`, { params: { days } }).then((r) => r.data),
};

// ---- Public
export const publicApi = {
  landingPage: (slug) => api.get(`/lp/${slug}`).then((r) => r.data),
  submitForm: (body) => api.post("/forms/submit", body).then((r) => r.data),
};

// ---- Uploads (chunked -> object storage)
export const uploadsApi = {
  list: () => api.get("/admin/uploads").then((r) => r.data),
  remove: (id) => api.delete(`/admin/uploads/${id}`).then((r) => r.data),
  init: (file, totalChunks) =>
    api
      .post("/admin/uploads/init", { filename: file.name, size: file.size, content_type: file.type, total_chunks: totalChunks })
      .then((r) => r.data),
  chunk: (uploadId, index, blob) =>
    api.put(`/admin/uploads/${uploadId}/chunks/${index}`, blob, { headers: { "Content-Type": "application/octet-stream" }, timeout: 120000 }).then((r) => r.data),
  complete: (uploadId) => api.post(`/admin/uploads/${uploadId}/complete`, null, { timeout: 180000 }).then((r) => r.data),
};

const CHUNK_SIZE = 1024 * 1024; // 1 MB chunks keep each request small for proxies

/**
 * Uploads an image in chunks. onProgress receives 0..100.
 * Resolves with the upload record { id, url, filename, ... }.
 */
export const uploadImage = async (file, onProgress = () => {}) => {
  const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
  onProgress(1);
  const { upload_id } = await uploadsApi.init(file, totalChunks);
  for (let i = 0; i < totalChunks; i += 1) {
    const blob = file.slice(i * CHUNK_SIZE, Math.min(file.size, (i + 1) * CHUNK_SIZE));
    await uploadsApi.chunk(upload_id, i, blob);
    onProgress(Math.round(((i + 1) / totalChunks) * 90));
  }
  const record = await uploadsApi.complete(upload_id);
  onProgress(100);
  return record;
};

/** Turns a stored media path (e.g. /api/uploads/x.jpg) into a loadable URL. */
export const resolveMedia = (url) => {
  if (!url) return "";
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
};

export default api;
